import { useContext } from 'react';
import { Restricted } from '@axo/shared/feature/restricted';
import { permissions } from '@axo/shared/data-access/types';
import { MarketCountryCode } from '@axo/shared/types';
import { NonIdealState, Tab, Tabs } from '@blueprintjs/core';
import {
  useHasAnyOfRoles,
  useIsManager,
} from '@axo/shared/data-access/provider';
import { BucketsTab } from './BucketsTab';
import { QuickSearchTab } from './QuickSearchTab';
import { ArchiveSearchTab } from './ArchiveSearchTab';
import { CustomFilterTab } from './CustomFilterTab';
import { WorkingListsTab } from './WorkingListsTab';
import { WorkQueuesTab } from './WorkQueuesTab';
import { CreateNewApplicationTab } from './CreateNewApplicationTab';
import { ApplicationContext } from '../../store/context';
import { useSearchTabActions, useSelectedSearchTab } from '../../store';
import { useShouldUseBuckets } from '../../hooks/utils/useShouldUseBuckets';
import styles from './Search.module.css';

export function Search() {
  const isManager = useIsManager();

  const tab = useSelectedSearchTab();
  const { selectTab } = useSearchTabActions();

  const { state } = useContext(ApplicationContext);

  const shouldUseBuckets = useShouldUseBuckets();

  const canUseQuickSearch = useHasAnyOfRoles([
    permissions.RoleName.Manager,
    permissions.RoleName.Sales,
    permissions.RoleName.SecuredSales,
    permissions.RoleName.Telemarketing,
  ]);

  const canUseArchiveSearch = useHasAnyOfRoles([
    permissions.RoleName.Manager,
    permissions.RoleName.Sales,
    permissions.RoleName.SecuredSales,
    permissions.RoleName.Telemarketing,
  ]);

  const canUseWorkingList = useHasAnyOfRoles([
    permissions.RoleName.Manager,
    permissions.RoleName.Sales,
    permissions.RoleName.SecuredSales,
    permissions.RoleName.Telemarketing,
    permissions.RoleName.TelemarketingLimited,
  ]);

  const hasUnrestrictedCustomFilterRole = useHasAnyOfRoles([
    permissions.RoleName.Manager,
    permissions.RoleName.SecuredSales,
    permissions.RoleName.Telemarketing,
  ]);

  const isSales = useHasAnyOfRoles([permissions.RoleName.Sales]);

  // Sales only keeps Custom Filter access in the FI and DK markets (MV-2921)
  const canUseCustomFilter =
    hasUnrestrictedCustomFilterRole ||
    (isSales &&
      (state.marketCountry === MarketCountryCode.Finland ||
        state.marketCountry === MarketCountryCode.Denmark));

  const canUseBuckets = useHasAnyOfRoles([permissions.RoleName.Manager]);

  const canUseWorkQueues = useHasAnyOfRoles([
    permissions.RoleName.Sales,
    permissions.RoleName.Manager,
  ]);

  const canCreateApplication = useHasAnyOfRoles([
    permissions.RoleName.Manager,
    permissions.RoleName.Sales,
    permissions.RoleName.Telemarketing,
    permissions.RoleName.TelemarketingLimited,
  ]);

  // The persisted tab may point to a tab the user is not allowed to open
  const selectedTab =
    tab === 'custom-filter' && !canUseCustomFilter ? 'working-list' : tab;

  if (!state.marketCountry) {
    return (
      <div className={styles.container}>
        <NonIdealState icon="flag">No market country selected</NonIdealState>
      </div>
    );
  }

  return (
    <Restricted
      isAllowedFor={[
        permissions.RoleName.Manager,
        permissions.RoleName.Sales,
        permissions.RoleName.SecuredSales,
        permissions.RoleName.Telemarketing,
        permissions.RoleName.TelemarketingLimited,
      ]}
    >
      <div className={styles.container}>
        <Tabs
          selectedTabId={selectedTab}
          onChange={selectTab}
          renderActiveTabPanelOnly
        >
          <Tab
            disabled={canUseQuickSearch !== true}
            id="quick-search"
            icon="search"
            title="Quick Search"
            panel={
              selectedTab === 'quick-search' ? (
                <QuickSearchTab marketCountry={state.marketCountry} />
              ) : undefined
            }
          />

          <Tab
            disabled={canUseWorkingList !== true}
            id="working-list"
            icon="list"
            title="Working List"
            panel={
              selectedTab === 'working-list' ? (
                <WorkingListsTab marketCountry={state.marketCountry} />
              ) : undefined
            }
          />

          <Tab
            disabled={canUseCustomFilter !== true}
            id="custom-filter"
            icon="filter"
            title="Custom Filter"
            hidden={!isManager}
            panel={
              selectedTab === 'custom-filter' ? (
                <CustomFilterTab marketCountry={state.marketCountry} />
              ) : undefined
            }
          />

          {shouldUseBuckets && (
            <Tab
              disabled={!canUseWorkQueues}
              id="work-queues"
              icon="sort"
              title="Work Queues"
              hidden={!canUseWorkQueues}
              panel={
                selectedTab === 'work-queues' ? (
                  <WorkQueuesTab marketCountry={state.marketCountry} />
                ) : undefined
              }
            />
          )}

          {shouldUseBuckets && (
            <Tab
              disabled={!canUseBuckets}
              id="buckets"
              icon="box"
              title="Buckets"
              hidden={!isManager}
              panel={
                selectedTab === 'buckets' ? (
                  <BucketsTab marketCountry={state.marketCountry} />
                ) : undefined
              }
            />
          )}

          <Tab
            disabled={canCreateApplication !== true}
            id="create-new-application"
            icon="dollar"
            title="Create new application"
            panel={
              selectedTab === 'create-new-application' ? (
                <CreateNewApplicationTab />
              ) : undefined
            }
          />

          <Tab
            disabled={canUseArchiveSearch !== true}
            id="archive-search"
            icon="data-search"
            title="Archive Search"
            panel={
              selectedTab === 'archive-search' ? (
                <ArchiveSearchTab marketCountry={state.marketCountry} />
              ) : undefined
            }
          />
        </Tabs>
      </div>
    </Restricted>
  );
}
